/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import WheelDisplay from '../components/wheels/WheelDisplay';
import { wheelsApi } from '../services/api';
import { formatWheelCreationDate, Wheel, WheelEntry, WheelEntryType } from '../types/wheel';

const ExploreWheels: React.FC = () => {
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('popular');
  // For tracking which wheels are currently spinning
  const [spinningWheels, setSpinningWheels] = useState<Record<string, boolean>>({});
  // For tracking spin results
  const [spinResults, setSpinResults] = useState<Record<string, WheelEntry | null>>({});
  // For tracking which wheels have been triggered to spin
  const [spinTriggers, setSpinTriggers] = useState<Record<string, boolean>>({});
  // For notification system
  const [notification, setNotification] = useState<{ message: string; visible: boolean }>({
    message: '',
    visible: false,
  });

  useEffect(() => {
    const fetchWheels = async () => {
      try {
        setLoading(true);
        const response = await wheelsApi.getAll();
        setWheels(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching wheels:', err);
        setError('Failed to load wheels. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchWheels();
  }, []);

  // Helper function to determine wheel difficulty based on entries
  const getDifficulty = (wheel: Wheel): { label: string; bolts: number } => {
    // Count punishments and high-value tasks
    const challengeCount = wheel.entries.filter(
      e =>
        e.type === WheelEntryType.CHALLENGE ||
        (e.type === WheelEntryType.TEXT &&
          e.text.includes('$') &&
          parseInt(e.text.match(/\$(\d+)/)?.[1] || '0') > 50)
    ).length;

    const difficultyRatio = challengeCount / wheel.entries.length;

    if (difficultyRatio > 0.7) return { label: 'Extreme', bolts: 4 };
    if (difficultyRatio > 0.4) return { label: 'Hard', bolts: 3 };
    if (difficultyRatio > 0.2) return { label: 'Medium', bolts: 2 };
    return { label: 'Easy', bolts: 1 };
  };

  // Filter wheels based on active category
  const filteredWheels = () => {
    switch (activeCategory) {
      case 'popular':
        return [...wheels].sort((a, b) => b.spinCount - a.spinCount);
      case 'newest':
        // Sort by creation date if available
        return [...wheels].sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          // Handle both Date and Timestamp objects
          const dateA =
            a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt.toString());
          const dateB =
            b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt.toString());
          return dateB.getTime() - dateA.getTime();
        });
      case 'random':
        return [...wheels].sort(() => Math.random() - 0.5);
      default:
        return wheels;
    }
  };

  // Quick spin functionality
  const handleQuickSpin = (wheel: Wheel) => {
    // Prevent spinning if already in progress
    if (spinningWheels[wheel.id]) return;

    // Mark this wheel as spinning
    setSpinningWheels(prev => ({ ...prev, [wheel.id]: true }));

    // Set the spin trigger for this wheel
    setSpinTriggers(prev => ({ ...prev, [wheel.id]: true }));

    // Clear previous result
    setSpinResults(prev => ({ ...prev, [wheel.id]: null }));
  };

  // Handle spin completion
  const handleSpinComplete = (wheel: Wheel, entry: WheelEntry) => {
    // Record the result
    setSpinResults(prev => ({ ...prev, [wheel.id]: entry }));

    // Mark wheel as no longer spinning
    setSpinningWheels(prev => ({ ...prev, [wheel.id]: false }));

    // Reset the spin trigger for this wheel
    setSpinTriggers(prev => ({ ...prev, [wheel.id]: false }));

    // Record the spin in the backend
    wheelsApi.spin(wheel.id, { entry_id: entry.id }).catch(err => {
      console.error('Error recording spin:', err);
    });

    // Update the wheel's spin count locally
    setWheels(prevWheels =>
      prevWheels.map(w => (w.id === wheel.id ? { ...w, spinCount: w.spinCount + 1 } : w))
    );
  };

  // Share functionality
  const handleShare = (wheel: Wheel) => {
    // Create the full URL to this wheel
    const wheelUrl = `${window.location.origin}/wheels/${wheel.id}`;

    // Use the Clipboard API to copy the URL
    navigator.clipboard
      .writeText(wheelUrl)
      .then(() => {
        // Show success notification
        setNotification({
          message: 'Wheel link copied to clipboard!',
          visible: true,
        });

        // Hide notification after 3 seconds
        setTimeout(() => {
          setNotification(prev => ({ ...prev, visible: false }));
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy URL:', err);
        // Show error notification
        setNotification({
          message: 'Failed to copy link. Please try again.',
          visible: true,
        });

        // Hide notification after 3 seconds
        setTimeout(() => {
          setNotification(prev => ({ ...prev, visible: false }));
        }, 3000);
      });
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-dark mb-2">Explore Wheels</h1>
            <p className="text-gray-600">Browse pre-made wheels or create your own custom wheel</p>
          </div>
          <Link to="/create-wheel" className="btn-primary mt-4 md:mt-0">
            Create New Wheel
          </Link>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {['popular', 'newest', 'random'].map(category => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full transition-all ${
                activeCategory === category
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && wheels.length === 0 && !error && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold mb-2">No wheels found</h3>
            <p className="text-gray-600 mb-6">Be the first to create a wheel!</p>
            <Link to="/create-wheel" className="btn-primary">
              Create New Wheel
            </Link>
          </div>
        )}

        {/* Wheels grid */}
        {!loading && wheels.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWheels().map(wheel => (
              <div
                key={wheel.id}
                className="card p-6 hover:shadow-lg transition-shadow rounded-xl bg-white border border-gray-100"
              >
                {/* Wheel Header */}
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-dark">{wheel.name}</h3>
                  <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full font-medium">
                    {wheel.spinCount} spins
                  </span>
                </div>

                {/* Creation Date */}
                <div className="text-sm text-gray-500 mb-4">
                  Created {wheel.createdAt ? formatWheelCreationDate(wheel) : 'N/A'}
                </div>

                {/* Wheel Display */}
                <div className="flex justify-center mb-4 relative">
                  <div className="w-48 h-48 transform hover:scale-105 transition-transform">
                    <WheelDisplay
                      entries={wheel.entries}
                      size={192}
                      colorScheme={wheel.theme}
                      showSpinButton={false}
                      externalSpin={spinTriggers[wheel.id] || false}
                      onSpinComplete={entry => handleSpinComplete(wheel, entry)}
                      hideResultDisplay={true} // Hide the internal result display
                    />
                  </div>
                  {/* Theme badge overlay removed as per requirements */}
                </div>

                {/* Wheel Info */}
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <span>
                    <span role="img" aria-label="Entries">
                      🎯
                    </span>{' '}
                    {wheel.entries.length} entries
                  </span>
                  <span
                    title={`Difficulty level: ${getDifficulty(wheel).label}`}
                    className="flex items-center"
                  >
                    <span role="img" aria-label="Difficulty" className="mr-1">
                      {Array(getDifficulty(wheel).bolts).fill('⚡').join('')}
                    </span>{' '}
                    {getDifficulty(wheel).label}
                  </span>
                </div>

                {/* Call to Action Buttons */}
                <div className="flex gap-2 mt-4">
                  {/* Quick Spin button */}
                  <button
                    onClick={() => handleQuickSpin(wheel)}
                    disabled={spinningWheels[wheel.id]}
                    className="flex-1 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    {spinningWheels[wheel.id] ? 'Spinning...' : 'Quick Spin'}
                  </button>

                  {/* View Wheel button */}
                  <Link
                    to={`/wheels/${wheel.id}`}
                    className="flex-1 py-2 text-sm text-center text-white bg-secondary rounded-lg hover:bg-secondary-dark transition-colors"
                  >
                    View Wheel
                  </Link>

                  {/* Share button */}
                  <button
                    onClick={() => handleShare(wheel)}
                    className="flex-1 py-2 text-sm text-white bg-accent rounded-lg hover:bg-accent-dark transition-colors"
                  >
                    Share
                  </button>
                </div>

                {/* Spin Result */}
                {spinResults[wheel.id] && (
                  <div className="mt-3 p-3 bg-gray-100 rounded-lg text-center">
                    <h4 className="text-sm font-medium text-gray-700">Result:</h4>
                    <div className="text-lg font-bold text-primary mt-1">
                      {spinResults[wheel.id]?.emoji && (
                        <span className="mr-2">{spinResults[wheel.id]?.emoji}</span>
                      )}
                      {spinResults[wheel.id]?.text}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Notification */}
      {notification.visible && (
        <div className="fixed bottom-4 right-4 bg-dark text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in">
          {notification.message}
        </div>
      )}
    </Layout>
  );
};

export default ExploreWheels;
